import writeXlsxFile from 'write-excel-file';
import { DateTimeHelpers } from 'src/app/shared/helpers/date-time.helpers';

import {
  VesselReportV202205,
  VesselReportedMainEngineBunkerConsumption,
  BunkerReportPart,
  VesselReportedPlan,
  StoppedStatus,
  UnknownMovingStatus,
  mapDirection,
  BunkerTypeMap,
  ManoeuvringStatusMap,
  BunkerReportPartMap
} from '../../services/noon-reports-v202205.service';

const capitalizeString = (str?: string) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';

const makeHeading = (value: string, span = 0) => ({
  type: String,
  value,
  height: 20,
  span,
  fontStyle: 'Calibri',
  fontSize: 11,
  align: 'center',
  alignVertical: 'center',
  backgroundColor: '#8DB4E2',
  borderStyle: 'thin',
});

const makeCell = (
  value: any = '',
  type: any = String,
  span = 0,
) => ({
  type,
  value,
  span,
  height: 20,
  fontStyle: 'Calibri',
  fontSize: 11,
  align: 'left',
  alignVertical: 'center',
  backgroundColor: '#FDFEE6',
  borderStyle: 'thin',
});

const exportSheetOne = (
  report: VesselReportV202205,
  organisationsMap?: Map<string, string>,
) => {
  let sourceName: string | undefined;
  let destinationName: string | undefined;
  let eta = '';
  let since = '';

  const a = report.currentPlan as
        | VesselReportedPlan
        | StoppedStatus
        | UnknownMovingStatus
        | undefined;
  if (a) {
    const a1 = (a as VesselReportedPlan).fromOrganisationId;
    if (a1 !== undefined) {sourceName = organisationsMap?.get(a1);}

    const a2 = (a as VesselReportedPlan).toOrganisationId;
    if (a2 !== undefined) {destinationName = organisationsMap?.get(a2);}

    const a3 = (a as VesselReportedPlan).eta;
    if (a3 !== undefined) {eta = DateTimeHelpers.getLocalDateIsoStringToShow(a3);}

    const a4 = (a as StoppedStatus).locationOrganisationId;
    if (a4 !== undefined) {
      sourceName = organisationsMap?.get(a4);
      destinationName = organisationsMap?.get(a4);
    }

    const a5 = (a as StoppedStatus).since;
    if (a5 !== undefined) {since = DateTimeHelpers.getLocalDateIsoStringToShow(a5);}
    if ((a as UnknownMovingStatus).description !== undefined) {
      sourceName = (a as UnknownMovingStatus).description;
      destinationName = '';
    }
  }

  const geo = report.vesselLocation?.geoCoordinate;
  const latitude = geo?.latitude
    ? `${geo.latitude.degree}° ${geo.latitude.minute}' ${geo.latitude.direction}`
    : '';
  const longitude = geo?.longitude
    ? `${geo.longitude.degree}° ${geo.longitude.minute}' ${geo.longitude.direction}`
    : '';

  const seaForce =
        report.weatherCondition?.seaForce?.force &&
            report.weatherCondition?.seaForce?.direction
          ? `${report.weatherCondition.seaForce.force} ${mapDirection(
            report.weatherCondition.seaForce.direction,
          )}`
          : '';

  const windForce =
        report.weatherCondition?.windForce?.force &&
            report.weatherCondition?.windForce?.direction
          ? `${report.weatherCondition.windForce.force} ${mapDirection(
            report.weatherCondition.windForce.direction,
          )}`
          : '';

  const currentForce =
        report.weatherCondition?.currentForce?.force &&
            report.weatherCondition?.currentForce?.direction
          ? `${report.weatherCondition.currentForce.force} ${mapDirection(
            report.weatherCondition.currentForce.direction,
          )}`
          : '';

  const swellForce =
        report.weatherCondition?.swellForce?.force &&
            report.weatherCondition?.swellForce?.direction
          ? `${report.weatherCondition.swellForce.force} ${mapDirection(
            report.weatherCondition.swellForce.direction,
          )}`
          : '';

  const tankeConditions = report.tanksCondition?.map((t) => ({
    name: `Tank No. ${t.tankNo} ${t.product}`,
    value: `${t.quantity} (Ton) ${t.temperature}°C`,
  }));

  const columns = [
    { width: 25 },
    { width: 25 },
    {},
    { width: 25 },
    { width: 25 },
    {},
    { width: 25 },
    { width: 15 },
    { width: 15 },
  ];

  const row1 = [
    {
      type: String,
      value: `${capitalizeString(report.reportType)} report ${DateTimeHelpers.isoDateStringFormatter(report.reportDate?.isoDate?.isoDate ?? '')}`,
      fontSize: 20,
      fontStyle: 'italic',
      span: 9,
      height: 40,
      align: 'center',
      alignVertical: 'center',
      borderStyle: 'thick',
    },
  ];

  const row2 = [null];

  const row3 = [
    makeHeading('Vessel Information', 2),
    null,
    null,
    makeHeading('Speed', 2),
    null,
    null,
    makeHeading('Lub Oil', 3),
    null,
    null,
  ];

  const row4 = [
    makeCell('Vessel Name'),
    makeCell(report.vesselInfo?.vesselName),
    null,
    makeCell('Current report speed'),
    makeCell(report.speed?.averageCurrentReportSpeed, Number),
    null,
    makeCell('Part'),
    makeCell('Consumption'),
    makeCell('ROB'),
  ];

  const row5 = [
    makeCell('Vessel IMO'),
    makeCell(report.vesselInfo?.vesselImo),
    null,
    makeCell('Voyage speed'),
    makeCell(report.speed?.averageVoyageSpeed, Number),
    null,
    makeCell('Main Engine Cylinder'),
    makeCell(report.lubOilStatus?.mainEngineCylinderOilConsumption, Number),
    makeCell(report.lubOilStatus?.mainEngineCylinderOilRob, Number),
  ];

  const row6 = [
    makeCell('Master'),
    makeCell(report.vesselInfo?.master),
    null,
    null,
    null,
    null,
    makeCell('Main Engine System'),
    makeCell(report.lubOilStatus?.mainEngineSystemOilConsumption, Number),
    makeCell(report.lubOilStatus?.mainEngineSystemOilRob, Number),
  ];

  const row7 = [
    null,
    null,
    null,
    makeHeading('Forces', 2),
    null,
    null,
    makeCell('Auxiliary System'),
    makeCell(report.lubOilStatus?.auxiliarySystemOilConsumption, Number),
    makeCell(report.lubOilStatus?.auxiliarySystemOilRob, Number),
  ];

  const row8 = [
    makeHeading('Current Plan', 2),
    null,
    null,
    makeCell('Sea force'),
    makeCell(seaForce),
    null,
  ];

  const row9 = [
    makeCell('Source'),
    makeCell(sourceName),
    null,
    makeCell('Wind force'),
    makeCell(windForce),
    null,
    makeHeading('Fresh water', 3),
    null,
    null,
  ];

  const row10 = [
    makeCell('Destination'),
    makeCell(destinationName),
    null,
    makeCell('Current force'),
    makeCell(currentForce),
    null,
    makeCell('Generated'),
    makeCell('Consumption'),
    makeCell('ROB'),
    null,
  ];

  const row11 = [
    makeCell(eta ? 'ETA' : 'Since'),
    makeCell(eta || since),
    null,
    makeCell('Swell force'),
    makeCell(swellForce),
    null,
    makeCell(report.freshWaterStatus?.generated, Number),
    makeCell(report.freshWaterStatus?.consumption, Number),
    makeCell(report.freshWaterStatus?.rob, Number),
    null,
  ];

  const row12 = [null];

  const row13 = [
    makeHeading('Distances', 2),
    null,
    null,
    makeHeading('Drafts', 2),
    null,
    null,
    makeHeading('Ballast water', 3),
    null,
    null,
  ];

  const row14 = [
    makeCell('Distance covered'),
    makeCell(report.distances?.distanceCovered, Number),
    null,
    makeCell('Aft draft'),
    makeCell(report.drafts?.aftDraft, Number),
    null,
    makeCell('Quantity'),
    makeCell(report.ballastWaterTotalVolume, Number, 2),
    null,
  ];

  const row15 = [
    makeCell('Distance By main engine'),
    makeCell(report.distances?.distanceByMainEngine, Number),
    null,
    makeCell('Middle draft'),
    makeCell(report.drafts?.middleDraft, Number),
  ];

  const row16 = [
    makeCell('Distance to go'),
    makeCell(report.distances?.distanceToGo, Number),
    null,
    makeCell('Forward draft'),
    makeCell(report.drafts?.forwardDraft, Number),
  ];

  const row17 = [
    makeCell('Total distance run'),
    makeCell(report.distances?.totalDistanceRun, Number),
    null,
  ];

  const row18 = [
    null,
    null,
    null,
    makeHeading('Cargo', 2),
    null,
    null,
    makeHeading('Comments', 3),
    null,
    null,
  ];

  const row19 = [
    makeHeading('Vessel location', 2),
    null,
    null,
    makeCell(tankeConditions ? tankeConditions[0]?.name : '', String),
    makeCell(tankeConditions ? tankeConditions[0]?.value : '', String),
    null,
    makeCell(report.comments, String, 3),
    null,
    null,
  ];

  const row20 = [
    makeCell('Latitude'),
    makeCell(latitude, String),
    null,
    makeCell(tankeConditions ? tankeConditions[1]?.name : '', String),
    makeCell(tankeConditions ? tankeConditions[1]?.value : '', String),
  ];

  const row21 = [
    makeCell('Longitude'),
    makeCell(longitude, String),
    null,
    makeCell(tankeConditions ? tankeConditions[2]?.name : '', String),
    makeCell(tankeConditions ? tankeConditions[2]?.value : '', String),
  ];

  const row22 = [
    makeCell('Bearing'),
    makeCell(report.vesselLocation?.bearing, Number),
    null,
    makeCell(tankeConditions ? tankeConditions[3]?.name : '', String),
    makeCell(tankeConditions ? tankeConditions[3]?.value : '', String),
  ];

  const row23 = [
    makeCell('Current location'),
    // makeCell(report.vesselLocation?.currentLocation),
    makeCell('BROKEN after move to GRPC'),
    null,
  ];

  const data = [
    row1,
    row2,
    row3,
    row4,
    row5,
    row6,
    row7,
    row8,
    row9,
    row10,
    row11,
    row12,
    row13,
    row14,
    row15,
    row16,
    row17,
    row18,
    row19,
    row20,
    row21,
    row22,
    row23,
  ];

  return { data, columns };
};

const exportBunkerSheet = (report: VesselReportV202205) => {
  const mainEngineRows =
        report.bunkerReports
          ?.filter(
            (b): b is VesselReportedMainEngineBunkerConsumption =>
              b.reportPart === BunkerReportPart.BUNKER_REPORT_PART_MAIN_ENGINE,
          )
          .map((b) => [
            makeCell(ManoeuvringStatusMap.get(b.manoeuvringStatus || 0)),
            makeCell(BunkerTypeMap.get(b.bunkerType)),
            makeCell((b.isLaden) ? 'Yes' : 'No'),
            makeCell(b.hours, Number),
            makeCell(b.averageRpm, Number),
            makeCell(b.quantityMt, Number),
          ]) ?? [];

  const otherRows =
        report.bunkerReports
          ?.filter((b) => b.reportPart !== BunkerReportPart.BUNKER_REPORT_PART_MAIN_ENGINE)
          .map((b) => [
            makeCell(BunkerReportPartMap.get(b.reportPart)),
            makeCell(BunkerTypeMap.get(b.bunkerType)),
            makeCell(b.quantityMt, Number),
          ]) ?? [];

  const columns = Array(6).fill({ width: 25 });

  const row1 = [makeHeading('Main engine Bunker consumption', 6)];
  const row2 = [
    makeCell('Manoeuvering Status'),
    makeCell('Bunker Type'),
    makeCell('Laden'),
    makeCell('Hours'),
    makeCell('Avg RPM'),
    makeCell('Quantity'),
  ];

  const row4 = [makeHeading('Other part Bunker consumption', 3)];
  const row5 = [makeCell('Part'), makeCell('Bunker Type'), makeCell('Quantity')];

  const data: any[] = [
    row1,
    row2,
    ...mainEngineRows,
    [],
    row4,
    row5,
    ...otherRows,
  ];

  return { data, columns };
};

export const exportExcel = async (
  report: VesselReportV202205,
  organisationsMap?: Map<string, string>,
) => {
  const sheet1 = exportSheetOne(report, organisationsMap);
  const sheet2 = exportBunkerSheet(report);
  const schema = {
    columns: [sheet1.columns, sheet2.columns],
    sheets: ['Information', 'Bunkers'],
    fileName:
            `${report.vesselInfo?.vesselName} ${report.reportType} ${DateTimeHelpers.isoDateStringFormatter(report.reportDate?.isoDate?.isoDate ?? '')}.xlsx`,
  };
  await writeXlsxFile([sheet1.data, sheet2.data], schema);
};
